import { useAccount } from '@/api/rest/users/useAccount';
import { ProductSpecCategory } from '@/gql/graphql';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';

/** Common subset of product-spec fields rendered in the readout. */
export type SpecForReadout = {
  id: string;
  name: string;
  category: ProductSpecCategory;
  manufacturer: string;
  modelNumber: string;
  image?: { id: string; url: string } | null;
  nominalOutputKw?: number | null;
  domesticHotWaterLitres?: number | null;
  scopAtDefaultFlow?: number | null;
  panelCapacityW?: number | null;
  acOutputKw?: number | null;
  hybrid?: boolean | null;
  usableCapacityKwh?: number | null;
  archivedAt?: string | null;
  dimensions?: {
    heightMm?: number | null;
    widthMm?: number | null;
    depthMm?: number | null;
  } | null;
};

const MM_PER_INCH = 25.4;

const formatLength = (
  mm: number | null | undefined,
  region: AccountRegions
) => {
  if (mm == null) return null;
  if (region === AccountRegions.US) {
    return `${Math.round((mm / MM_PER_INCH) * 10) / 10} in`;
  }
  return `${Math.round(mm)} mm`;
};

const hybridLabel = (hybrid?: boolean | null): string | null => {
  if (hybrid == null) return null;
  return hybrid ? 'Yes' : 'No';
};

const SpecReadout: FC<{ spec: SpecForReadout | null }> = ({ spec }) => {
  const region = useAccount()?.region ?? AccountRegions.UK;

  if (!spec) return null;

  // Solar panel specs carry no depth, so that row would always be empty.
  const heightWidthRows: [string, string | null][] = [
    ['Height', formatLength(spec.dimensions?.heightMm, region)],
    ['Width', formatLength(spec.dimensions?.widthMm, region)],
  ];
  const dimensionRows: [string, string | null][] = [
    ...heightWidthRows,
    ['Depth', formatLength(spec.dimensions?.depthMm, region)],
  ];
  let rows: [string, string | null][];
  switch (spec.category) {
    case 'CYLINDER':
      rows = [
        [
          'Capacity',
          spec.domesticHotWaterLitres != null
            ? `${spec.domesticHotWaterLitres} L`
            : null,
        ],
        ...dimensionRows,
      ];
      break;
    case 'SOLAR_PANEL':
      rows = [
        [
          'Capacity',
          spec.panelCapacityW != null ? `${spec.panelCapacityW} W` : null,
        ],
        ...heightWidthRows,
      ];
      break;
    case 'INVERTER':
      rows = [
        ['AC output', spec.acOutputKw != null ? `${spec.acOutputKw} kW` : null],
        ['Hybrid', hybridLabel(spec.hybrid)],
        ...dimensionRows,
      ];
      break;
    case 'BATTERY':
      rows = [
        [
          'Usable capacity',
          spec.usableCapacityKwh != null
            ? `${spec.usableCapacityKwh} kWh`
            : null,
        ],
        ...dimensionRows,
      ];
      break;
    default:
      rows = [
        [
          'Heat output',
          spec.nominalOutputKw != null ? `${spec.nominalOutputKw} kW` : null,
        ],
        [
          'SCOP',
          spec.scopAtDefaultFlow != null
            ? String(spec.scopAtDefaultFlow)
            : null,
        ],
        ...dimensionRows,
      ];
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="-mx-5 mb-4 flex items-center gap-3 border-b border-gray-200 px-5 pb-4">
        {spec.image?.url ? (
          <img
            src={spec.image.url}
            alt=""
            className="h-16 w-16 flex-shrink-0 rounded-md border border-gray-200 object-contain"
          />
        ) : (
          <span className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-gray-400">
            <UntitledIcon name="box.3" className="h-6 w-6" />
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-bold">{spec.name}</p>
          <p className="truncate text-gray-500">
            {spec.manufacturer} · {spec.modelNumber}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-7 gap-y-4 lg:grid-cols-3">
        {rows.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <p className="text-gray-500">{label}</p>
            <p className="font-medium">{value ?? '-'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpecReadout;
